import { create } from "zustand"
import { persist } from "zustand/middleware"
import { $publicApi, $privateApi } from "config/api"
import { MAX_SYMPTOMS, RTL_LANGUAGES } from "../config/constants"
import { diagnosticToString } from "../config/helpers"

const lang = localStorage.getItem("language") || navigator.language.split("-")[0] || "en"

const initialState = {
  user: {
    first_name: "",
    last_name: "",
    birthday: "",
    gender: "",
    weight: "",
    height: "",
    diabetes: null,
    cholesterol: null,
    hypertension: null,
    injury: null,
  },
  anamnesis: {
    uuid: "",
    symptoms: [],
    questions: [],
    diseases: [],
    bodyPart: {},
    symptomsData: {},
    currentStep: 0,
    currentQuestion: {},
    exitStatus: {},
    activeDisease: null,
    selected: "",
  },
  meta: {
    open_drawer: false,
    show_backdrop: false,
    lang: lang,
    direction: RTL_LANGUAGES.includes(lang) ? "rtl" : "ltr",
    mode: "light",
  },
  fields: {
    first_name: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    last_name: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    phone_number: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    email: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    email_confirmation_code: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    birthday: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    age: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    gender: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    weight: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    height: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    diabetes: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    hypertension: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    cholesterol: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    injury: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    password: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    password_confirmation: {
      input_value: "",
      touched: false,
      error_message: "",
    },

    terms_and_conditions: {
      input_value: true,
      touched: true,
      error_message: "",
    },

    privacy_data: {
      input_value: true,
      touched: true,
      error_message: "",
    },

    country_code: {
      input_value: "+971",
      touched: true,
      error_message: "",
    },
  },
  translations: {},
}

const useAppStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      resetState: () =>
        set((state) => {
          return {
            user: { ...initialState.user },
            anamnesis: { ...initialState.user },
            fields: { ...initialState.fields },
            meta: { ...state.meta },
            translations: { ...state.translations },
          }
        }),

      // Fields
      resetFields: () =>
        set(() => {
          return { fields: { ...initialState.fields } }
        }),

      syncFields: (fields) => {
        get().resetFields()
        Object.entries(fields).forEach(([key, value]) => {
          if (key === "phone_number") {
            const match = value.match(/^(\+\d+)(.*)/)

            if (match) {
              get().updateFieldInputValue("country_code", match[1])
              get().updateFieldTouched("country_code", true)
              get().updateFieldErrorMessage(key, "")
              get().updateFieldInputValue("phone_number", match[2].trim())
              get().updateFieldTouched(key, true)
              get().updateFieldErrorMessage(key, "")
            } else {
              get().updateFieldInputValue("phone_number", value)
              get().updateFieldTouched(key, true)
              get().updateFieldErrorMessage(key, "")
            }
          } else if (["cholesterol", "hypertension", "injury", "diabetes"].includes(key)) {
            get().updateFieldInputValue(key, diagnosticToString(value))
            get().updateFieldTouched(key, true)
            get().updateFieldErrorMessage(key, "")
          } else {
            get().updateFieldInputValue(key, value)
            get().updateFieldTouched(key, true)
            get().updateFieldErrorMessage(key, "")
          }
        })
      },

      updateFieldErrorMessage: (key, value) =>
        set((state) => {
          return {
            fields: { ...state.fields, [key]: { ...state.fields[key], error_message: value } },
          }
        }),

      updateFieldInputValue: (key, value) =>
        set((state) => {
          let formattedValue

          if (value === null || value === 0) {
            formattedValue = ""
          } else if (value === true || value === false) {
            formattedValue = value
          } else {
            formattedValue = String(value)
          }

          return {
            fields: {
              ...state.fields,
              [key]: { ...state.fields[key], input_value: formattedValue },
            },
          }
        }),

      updateFieldTouched: (key, value) =>
        set((state) => {
          return { fields: { ...state.fields, [key]: { ...state.fields[key], touched: value } } }
        }),

      // Client
      syncClient: (data) => {
        set((state) => {
          get().resetClient()
          return { user: { ...state.user, ...data } }
        })
      },

      resetClient: () => {
        set(() => {
          return { user: { ...initialState.user } }
        })
      },

      createClient: (params) => {
        return $publicApi.post("/auth/registration", params)
      },

      login: (params) => {
        return $privateApi.post("/auth/login", params)
      },

      logout: () => {
        return $privateApi.delete("/auth/logout")
      },

      resetPassword: (params) => {
        return $publicApi.post("/client/password/reset", params)
      },

      updatePassword: (params) => {
        return $privateApi.post("/client/password", params)
      },

      getClientProfile: (params) => {
        return $privateApi.get("/clients/profile", params)
      },

      updateClientInfo: (params) => {
        return $privateApi.patch("/clients/profile", params)
      },

      confirmEmail: (params) => {
        return $publicApi.post("/auth/email_confirmation", params)
      },

      getClientAnamneses: () => {
        get().toggleBackdrop(true)
        return $privateApi.get("/clients/anamneses").finally(() => get().toggleBackdrop(false))
      },

      getClientAnamnesis: (id) => {
        get().toggleBackdrop(true)
        return $privateApi
          .get(`/clients/anamnesis/${id}`)
          .finally(() => get().toggleBackdrop(false))
      },

      // Anamnesis
      resetAnamnesis: () =>
        set(() => {
          return { anamnesis: { ...initialState.anamnesis } }
        }),

      getAnamnesis: () => {
        return $privateApi
          .get(`clients/anamnesis/${get().anamnesis.uuid}`)
          .then((response) => response.data)
          .then((data) => {
            set((state) => {
              return { anamnesis: { ...state.anamnesis, ...data } }
            })
          })
      },

      createAnamnesis: () => {
        get().toggleBackdrop(true)

        return $privateApi
          .post(`clients/anamnesis`)
          .then((response) => response.data)
          .then((data) => {
            set((state) => {
              return { anamnesis: { ...state.anamnesis, ...data } }
            })
            get().syncFields(data)
          })
          .finally(() => get().toggleBackdrop(false))
      },

      updateAnamnesis: (params) => {
        get().toggleBackdrop(true)

        return $privateApi
          .put(`clients/anamnesis/${get().anamnesis.uuid}`, params)
          .then((response) => response.data)
          .then((data) => {
            set((state) => {
              return { anamnesis: { ...state.anamnesis, ...data } }
            })
          })
          .finally(() => get().toggleBackdrop(false))
      },

      createSymptom: (params) => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length) {
          return Promise.resolve({ data: [] })
        }
        get().toggleBackdrop(true)

        return $privateApi
          .post(`clients/anamnesis/${uuid}/symptoms`, params)
          .then((response) => response.data)
          .then((data) =>
            set((state) => {
              const maxData = data.slice(0, MAX_SYMPTOMS)

              return { anamnesis: { ...state.anamnesis, symptoms: [...maxData] } }
            }),
          )
          .finally(() => get().toggleBackdrop(false))
      },

      searchSymptoms: (query) => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length || !query.length) {
          return Promise.resolve({ data: [] })
        }

        return $privateApi.get(`/info/symptoms?anamnesis=${uuid}&query=${query}`)
      },

      searchSymptomsByBodyPart: (bodyPartIds) => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length || !Number.isInteger(bodyPartIds)) {
          return Promise.resolve({ data: [] })
        }

        return $privateApi.get(`/info/symptoms?anamnesis=${uuid}&body_part=${bodyPartIds}`)
      },

      searchSymptomsByAi: (query) => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length || !query.length) {
          return Promise.resolve({ data: [] })
        }

        return $privateApi.get(`/info/symptoms?anamnesis=${uuid}&query=${query}&ai=true`)
      },

      getBodyPart: (bodyPartId) => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length || !Number.isInteger(bodyPartId)) {
          return Promise.resolve({ data: [] })
        }

        get().toggleBackdrop(true)

        return $privateApi
          .get(`/info/body_part/${bodyPartId}`)
          .then((response) => response.data)
          .then((data) => {
            set((state) => {
              return { anamnesis: { ...state.anamnesis, bodyPart: { ...data } } }
            })
          })
          .finally(() => get().toggleBackdrop(false))
      },

      getCountryCodes: () => {
        return $privateApi.get(`/info/country_codes`).then((response) => response.data)
      },

      getSymptoms: () => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length) {
          return Promise.resolve({ data: [] })
        }

        get().toggleBackdrop(true)

        return $privateApi
          .get(`/clients/anamnesis/${uuid}/symptoms`)
          .then((response) => response.data)
          .then((data) =>
            set((state) => {
              return { anamnesis: { ...state.anamnesis, symptoms: [...data] } }
            }),
          )
          .finally(() => get().toggleBackdrop(false))
      },

      updateSymptomsData: (data) =>
        set((state) => {
          return { anamnesis: { ...state.anamnesis, symptomsData: { ...data } } }
        }),

      deleteSymptom: (id) => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length || typeof id != "number") {
          return Promise.resolve({ data: [] })
        }

        const params = { params: { symptom_id: id } }

        return $privateApi
          .delete(`/clients/anamnesis/${uuid}/symptoms`, params)
          .then(() => get().getSymptoms())
      },

      getNextQuestion: () => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length) {
          return Promise.resolve({ data: [] })
        }

        get().toggleBackdrop(true)

        return $privateApi
          .post(`/clients/anamnesis/${uuid}/next_question`)
          .then((response) => response.data)
          .then((data) =>
            set((state) => {
              return {
                anamnesis: {
                  ...state.anamnesis,
                  currentQuestion: data.question,
                  exitStatus: data.exit,
                },
              }
            }),
          )
          .then(() => get().anamnesis.exitStatus)
          .finally(() => get().toggleBackdrop(false))
      },

      getDiseases: () => {
        const uuid = get().anamnesis.uuid

        if (!uuid?.length) {
          return Promise.resolve({ data: [] })
        }

        get().toggleBackdrop(true)

        return $privateApi
          .post(`/clients/anamnesis/${uuid}/diagnose`)
          .then((response) => response.data)
          .then((data) =>
            set((state) => {
              console.log(data)
              return {
                anamnesis: {
                  ...state.anamnesis,
                  diseases: data,
                },
              }
            }),
          )
          .then(() => get().anamnesis.diseases)
          .finally(() => get().toggleBackdrop(false))
      },

      nextAnamnesisStep: () =>
        set((state) => {
          return { anamnesis: { ...state.anamnesis, currentStep: get().anamnesis.currentStep + 1 } }
        }),

      prevAnamnesisStep: () =>
        set((state) => {
          return { anamnesis: { ...state.anamnesis, currentStep: get().anamnesis.currentStep - 1 } }
        }),

      finalStep: () =>
        set((state) => {
          return { anamnesis: { ...state.anamnesis, currentStep: 4 } }
        }),

      bodyPartSelect: (bodyPartId) => {
        set((state) => {
          return { anamnesis: { ...state.anamnesis, selected: bodyPartId } }
        })
      },

      // App
      toggleDrawer: (condition) =>
        set((state) => {
          return { meta: { ...state.meta, open_drawer: condition } }
        }),

      toggleBackdrop: (condition) =>
        set((state) => {
          return { meta: { ...state.meta, show_backdrop: condition } }
        }),

      setActiveDisease: (obj) => {
        set((state) => {
          return { anamnesis: { ...state.anamnesis, activeDisease: obj } }
        })
      },

      selectLanguage: (lang) =>
        set((state) => {
          const direction = RTL_LANGUAGES.includes(lang) ? "rtl" : "ltr"

          return { meta: { ...state.meta, lang, direction } }
        }),

      setMode: (mode) =>
        set((state) => {
          return { meta: { ...state.meta, mode } }
        }),

      setSystemTheme: () => {
        const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches

        set((state) => {
          return { meta: { ...state.meta, mode: isDark ? "dark" : "light" } }
        })
      },

      syncTranslations: () => {
        // get().toggleBackdrop(true)

        return $privateApi
          .get(`/info/translations`)
          .then((response) => response.data)
          .then((data) =>
            set(() => {
              return { translations: data }
            }),
          )
        // .finally(() => get().toggleBackdrop(false))
      },
    }),
    {
      name: "app", // name of the item in the storage (must be unique)
      // storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
    },
  ),
)

export default useAppStore
